/**
 * SupportPage - formulaire de support + liste des tickets du client.
 *  - Formulaire de creation (avec piece jointe optionnelle uploadee en Blob).
 *  - Liste "Mes tickets" : tous les tickets du client avec leur statut.
 */
import { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card, CardHeader, Title3, Body1, Caption1, Button, Input, Textarea,
  Spinner, MessageBar, MessageBarBody, MessageBarTitle,
  Dropdown, Option, Field, Badge,
  Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell,
  tokens,
} from '@fluentui/react-components';
import {
  Person24Regular, Mail24Regular, Phone24Regular,
  ChatHelp24Regular, CheckmarkCircle24Filled, Attach20Regular, Dismiss16Regular,
  DocumentArrowDown16Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../auth/AuthContext';
import { supportApi, type SupportCategory, type SupportLanguage, type SupportTicket } from '../api/supportApi';

const CATEGORIES: SupportCategory[] = ['technical', 'bug', 'compliance', 'billing', 'feature', 'other'];
const LANGUAGES: SupportLanguage[]  = ['fr-BE', 'nl-BE', 'de-BE', 'en'];

const MAX_ATTACHMENT_MB = 10;
const ALLOWED_EXT = '.png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.log,.csv,.doc,.docx,.xls,.xlsx,.zip';

const STATUS_COLOR: Record<string, 'warning' | 'brand' | 'success' | 'informative' | 'subtle'> = {
  open:        'warning',
  in_progress: 'brand',
  resolved:    'success',
  closed:      'informative',
};

export function SupportPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [category, setCategory]       = useState<SupportCategory>('technical');
  const [email, setEmail]             = useState(user?.email ?? '');
  const [name, setName]               = useState(user?.name ?? '');
  const [phone, setPhone]             = useState('');
  const [language, setLanguage]       = useState<SupportLanguage>(
    (LANGUAGES.includes(i18n.language as SupportLanguage) ? i18n.language : 'fr-BE') as SupportLanguage,
  );
  const [description, setDescription] = useState('');
  const [file, setFile]               = useState<File | null>(null);
  const fileInputRef                  = useRef<HTMLInputElement>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [ticketId, setTicketId]     = useState<string | null>(null);
  const [closingId, setClosingId]   = useState<string | null>(null);

  const ticketsQ = useQuery({
    queryKey:  ['my-support-tickets'],
    queryFn:   () => supportApi.listMyTickets(),
    staleTime: 30_000,
    retry:     false,
  });
  const tickets = [...(ticketsQ.data?.tickets ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  function resetForm() {
    setCategory('technical');
    setPhone('');
    setDescription('');
    setFile(null);
    setTicketId(null);
    setError(null);
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    if (f && f.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
      setError(t('support.form.attachmentTooBig', { max: MAX_ATTACHMENT_MB }));
      e.target.value = '';
      return;
    }
    setError(null);
    setFile(f);
  }

  async function uploadAttachment(): Promise<{ url: string; name: string } | null> {
    if (!file) return null;
    const sas = await supportApi.getAttachmentSas(file.name);
    const put = await fetch(sas.uploadUrl, {
      method:  'PUT',
      headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type || 'application/octet-stream' },
      body:    file,
    });
    if (!put.ok) throw new Error(t('support.form.attachmentUploadFailed', 'Échec de l’envoi de la pièce jointe.'));
    return { url: sas.readUrl, name: file.name };
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { setError(t('support.errorEmail')); return; }
    if (description.trim().length < 10) { setError(t('support.errorDescription')); return; }

    setSubmitting(true);
    try {
      const attachment = await uploadAttachment();
      const res = await supportApi.createTicket({
        category,
        email:          email.trim(),
        name:           name.trim(),
        phone:          phone.trim(),
        language,
        description:    description.trim(),
        attachmentUrl:  attachment?.url,
        attachmentName: attachment?.name,
      });
      setTicketId(res.ticketId);
      void queryClient.invalidateQueries({ queryKey: ['my-support-tickets'] });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function onCloseTicket(id: string) {
    setClosingId(id);
    try {
      await supportApi.closeTicket(id);
      await ticketsQ.refetch();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setClosingId(null);
    }
  }

  return (
    <div>
      <PageHeader title={t('support.title')} description={t('support.subtitle')} />

      {ticketId ? (
        <Card style={{ padding: 24, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <CheckmarkCircle24Filled style={{ color: tokens.colorPaletteGreenForeground1 }} />
            <Title3 style={{ color: tokens.colorPaletteGreenForeground1 }}>{t('support.success.title')}</Title3>
          </div>
          <Body1 block style={{ marginBottom: 8 }}>{t('support.success.body', { ticketId })}</Body1>
          <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3, marginBottom: 16 }}>
            {t('support.success.emailNotice', { email })}
          </Caption1>
          <Button appearance="primary" onClick={resetForm}>{t('support.success.newTicket')}</Button>
        </Card>
      ) : (
        <Card style={{ marginBottom: 24 }}>
          <CardHeader
            image={<ChatHelp24Regular primaryFill={tokens.colorBrandForeground1} />}
            header={<Title3>{t('support.form.title')}</Title3>}
          />
          <form onSubmit={onSubmit} style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Field label={t('support.form.category')} required hint={t('support.form.categoryHint')}>
              <Dropdown
                value={t(`support.category.${category}`)}
                selectedOptions={[category]}
                onOptionSelect={(_, d) => setCategory((d.optionValue as SupportCategory) ?? 'technical')}
              >
                {CATEGORIES.map((c) => (
                  <Option key={c} value={c} text={t(`support.category.${c}`)}>{t(`support.category.${c}`)}</Option>
                ))}
              </Dropdown>
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
              <Field label={t('support.form.email')} required>
                <Input type="email" value={email} onChange={(_, d) => setEmail(d.value)} contentBefore={<Mail24Regular />} placeholder="vous@entreprise.be" required />
              </Field>
              <Field label={t('support.form.name')}>
                <Input value={name} onChange={(_, d) => setName(d.value)} contentBefore={<Person24Regular />} />
              </Field>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
              <Field label={t('support.form.phone')} hint={t('support.form.phoneHint')}>
                <Input type="tel" value={phone} onChange={(_, d) => setPhone(d.value)} contentBefore={<Phone24Regular />} placeholder="+32 …" />
              </Field>
              <Field label={t('support.form.language')}>
                <Dropdown
                  value={t(`support.language.${language}`)}
                  selectedOptions={[language]}
                  onOptionSelect={(_, d) => setLanguage((d.optionValue as SupportLanguage) ?? 'fr-BE')}
                >
                  {LANGUAGES.map((l) => (
                    <Option key={l} value={l} text={t(`support.language.${l}`)}>{t(`support.language.${l}`)}</Option>
                  ))}
                </Dropdown>
              </Field>
            </div>

            <Field label={t('support.form.description')} required hint={t('support.form.descriptionHint')}>
              <Textarea value={description} onChange={(_, d) => setDescription(d.value)} rows={8} placeholder={t('support.form.descriptionPlaceholder')} required />
            </Field>

            <Field label={t('support.form.attachment', 'Pièce jointe (optionnelle)')} hint={t('support.form.attachmentHint', 'Max 10 Mo · images, PDF, Office, txt, zip')}>
              {file ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Attach20Regular />
                  <Body1 style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name} <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>({(file.size / 1024 / 1024).toFixed(1)} Mo)</Caption1>
                  </Body1>
                  <Button appearance="subtle" size="small" icon={<Dismiss16Regular />} onClick={() => setFile(null)}>
                    {t('common.remove', 'Retirer')}
                  </Button>
                </div>
              ) : (
                <div>
                  <Button appearance="secondary" icon={<Attach20Regular />} style={{ width: 'fit-content' }} onClick={() => fileInputRef.current?.click()}>
                    {t('support.form.attachmentChoose', 'Choisir un fichier')}
                  </Button>
                  <input ref={fileInputRef} type="file" accept={ALLOWED_EXT} onChange={onPickFile} style={{ display: 'none' }} />
                </div>
              )}
            </Field>

            {error && (
              <MessageBar intent="error">
                <MessageBarBody>
                  <MessageBarTitle>{t('common.errorTitle')}</MessageBarTitle>
                  {error}
                </MessageBarBody>
              </MessageBar>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Button appearance="primary" type="submit" disabled={submitting || !email || !description} icon={submitting ? <Spinner size="tiny" /> : undefined}>
                {submitting ? t('support.form.submitting') : t('support.form.submit')}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Mes tickets */}
      <Card>
        <CardHeader header={<Title3>{t('support.myTickets.title', 'Mes tickets')}</Title3>} />
        {ticketsQ.isLoading ? (
          <Spinner size="tiny" style={{ marginTop: 12 }} label={t('common.loading', 'Chargement…')} />
        ) : tickets.length === 0 ? (
          <Body1 block style={{ marginTop: 12, color: tokens.colorNeutralForeground3 }}>
            {t('support.myTickets.empty', 'Aucun ticket pour le moment.')}
          </Body1>
        ) : (
          <Table size="small" style={{ marginTop: 8 }}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('support.myTickets.ref', 'Référence')}</TableHeaderCell>
                <TableHeaderCell>{t('support.form.category')}</TableHeaderCell>
                <TableHeaderCell>{t('support.myTickets.status', 'Statut')}</TableHeaderCell>
                <TableHeaderCell>{t('support.myTickets.date', 'Date')}</TableHeaderCell>
                <TableHeaderCell>{t('support.form.attachment', 'Pièce jointe')}</TableHeaderCell>
                <TableHeaderCell style={{ width: 110 }}></TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tickets.map((tk: SupportTicket) => (
                <TableRow key={tk.id}>
                  <TableCell><span style={{ fontFamily: 'Consolas, monospace', fontWeight: 600 }}>{tk.id}</span></TableCell>
                  <TableCell>{t(`support.category.${tk.category}`)}</TableCell>
                  <TableCell>
                    <Badge appearance="filled" color={STATUS_COLOR[tk.status] ?? 'informative'}>
                      {t(`support.status.${tk.status}`, tk.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(tk.createdAt).toLocaleString(i18n.language)}</TableCell>
                  <TableCell>
                    {tk.attachmentUrl ? (
                      <a href={tk.attachmentUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <DocumentArrowDown16Regular /> {tk.attachmentName ?? t('support.form.attachment', 'Pièce jointe')}
                      </a>
                    ) : (
                      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>—</Caption1>
                    )}
                  </TableCell>
                  <TableCell>
                    {tk.status !== 'closed' && (
                      <Button
                        appearance="subtle" size="small" icon={<Dismiss16Regular />}
                        disabled={closingId === tk.id}
                        onClick={() => onCloseTicket(tk.id)}
                      >
                        {closingId === tk.id ? t('support.myTickets.closing', 'Fermeture…') : t('support.myTickets.close', 'Fermer')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
